import React, { useState, useMemo, useEffect } from 'react';
import { useCart } from '@/contexts/CartContext';
import { ToastContext } from '@/components/ToastProvider';

export default function QuickViewModal({ product, isOpen, onClose }) {
  // Use the product's quantityStep field, fallback to 0.25 for backward compatibility
  const defaultStep = Number(product?.quantityStep) || 0.25;
  const defaultInitialQuantity = defaultStep;

  const [quantity, setQuantity] = useState(defaultInitialQuantity);
  const { addItem } = useCart();
  const { notify } = React.useContext(ToastContext);

  // Reset quantity when product changes
  useEffect(() => {
    setQuantity(defaultInitialQuantity);
  }, [product?.id, defaultInitialQuantity]);

  if (!isOpen || !product) return null;

  const handleAddToCart = () => {
    addItem({ ...product, qty: quantity });
    notify(`${product.name} added to cart!`, 'success');
    onClose();
  };

  const handleQuantityChange = (e) => {
    const value = parseFloat(e.target.value);
    if (value >= defaultStep) {
      setQuantity(value);
    } else {
      setQuantity(defaultStep);
    }
  };

  const handleQuantityIncrement = () => {
    setQuantity(prev => Math.min(prev + defaultStep, stockNum));
  };

  const handleQuantityDecrement = () => {
    setQuantity(prev => Math.max(defaultStep, prev - defaultStep));
  };

  const getFirstImage = (prod) => {
    if (typeof prod?.imageUrl === "string" && prod.imageUrl.trim()) return prod.imageUrl.trim();
    if (typeof prod?.thumbnail === "string" && prod.thumbnail.trim()) return prod.thumbnail.trim();
    if (Array.isArray(prod?.images) && prod.images.length && typeof prod.images[0] === "string") {
      return prod.images[0].trim();
    }
    return "";
  };

  const image = getFirstImage(product);
  const price = Number(product?.price || 0);
  const compareAt = Number(product?.compareAtPrice || 0);
  const onSale = compareAt > price;
  const stockNum = Number(product?.stock ?? 0);
  const isOut = stockNum <= 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Quick View</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Image */}
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                {image ? (
                  <img
                    src={image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    No Image
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="space-y-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{product.name}</h2>
                  {product.sku && (
                    <p className="text-sm text-gray-500">SKU: {product.sku}</p>
                  )}
                </div>

                {/* Price */}
                <div className="flex items-center space-x-2">
                  <span className="text-2xl font-bold text-green-600">
                    ₱{price.toLocaleString()}
                  </span>
                  {onSale && (
                    <span className="text-lg text-gray-500 line-through">
                      ₱{compareAt.toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Stock Status */}
                <div>
                  {isOut ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Out of Stock
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      In Stock ({stockNum})
                    </span>
                  )}
                </div>

                {/* Description */}
                {product.description && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                    <p className="text-gray-600 text-sm">{product.description}</p>
                  </div>
                )}

                {/* Categories */}
                {product.categories && product.categories.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Categories</h4>
                    <div className="flex flex-wrap gap-2">
                      {product.categories.map((category, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add to Cart Section */}
                {!isOut && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center space-x-4">
                      <label className="text-sm font-medium text-gray-700">Quantity:</label>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleQuantityDecrement}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                          title="Decrease quantity"
                        >
                          -
                        </button>
                                                 <input
                           type="number"
                           min={defaultStep}
                           max={stockNum}
                           step={defaultStep}
                           value={quantity}
                           onChange={handleQuantityChange}
                           className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center"
                         />
                        <button
                          onClick={handleQuantityIncrement}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                          title="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                                             <span className="text-sm text-gray-500">
                         ({defaultStep === 0.25 ? '0.25 increments' : `${defaultStep} unit increments`})
                       </span>
                    </div>
                    
                    <button
                      onClick={handleAddToCart}
                      className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      Add to Cart
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
